// Phase N follow-up — super-admin audit page for the AI template generator.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Sparkles, CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 50;

const STATUS_TONE = {
    ok: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    rate_limited: { icon: ShieldAlert, cls: "bg-amber-50 text-amber-800 border-amber-200" },
    failed: { icon: AlertTriangle, cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

function StatusChip({ status }) {
    const t = STATUS_TONE[status] || STATUS_TONE.failed;
    const Icon = t.icon;
    return (
        <Badge
            variant="outline"
            className={`gap-1 ${t.cls}`}
            data-testid={`tgl-status-${status}`}
        >
            <Icon size={11} />
            {status}
        </Badge>
    );
}

export default function TemplateGeneratorAuditPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [statusFilter, setStatusFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, offset };
            if (statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/admin/template-generation-log", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, statusFilter]);

    const okCount = items.filter((r) => r.status === "ok").length;
    const totalTokens = items.reduce((sum, r) => sum + (r.tokens_used || 0), 0);

    return (
        <DashboardShell>
            <div className="space-y-5" data-testid="template-generator-audit-page">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="space-y-1">
                        <Link
                            to="/admin/settings"
                            className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-blue-900 hover:underline"
                        >
                            <ChevronLeft size={12} /> All settings
                        </Link>
                        <h1 className="text-3xl font-display flex items-center gap-2">
                            <Sparkles size={22} className="text-blue-900" /> AI generator audit log
                        </h1>
                        <p className="text-sm text-slate-500 max-w-2xl">
                            Every call to the invoice-template AI generator — successful, throttled, or failed — with the prompt inputs and token spend. Super-admin only.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={statusFilter} onValueChange={(v) => { setOffset(0); setStatusFilter(v); }}>
                            <SelectTrigger className="w-44" data-testid="tgl-status-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="ok">OK</SelectItem>
                                <SelectItem value="rate_limited">Rate limited</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Card data-testid="tgl-card-total">
                        <CardContent className="p-4 space-y-1">
                            <div className="text-xs uppercase tracking-wide text-slate-500">Total logged</div>
                            <div className="text-2xl font-display">{total.toLocaleString()}</div>
                        </CardContent>
                    </Card>
                    <Card data-testid="tgl-card-ok">
                        <CardContent className="p-4 space-y-1">
                            <div className="text-xs uppercase tracking-wide text-slate-500">On this page · status=ok</div>
                            <div className="text-2xl font-display">{okCount.toLocaleString()}</div>
                        </CardContent>
                    </Card>
                    <Card data-testid="tgl-card-tokens">
                        <CardContent className="p-4 space-y-1">
                            <div className="text-xs uppercase tracking-wide text-slate-500">Tokens on this page</div>
                            <div className="text-2xl font-display">{totalTokens.toLocaleString()}</div>
                        </CardContent>
                    </Card>
                </div>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Generation log</CardTitle>
                    </CardHeader>
                    <CardContent className="px-0">
                        <Table data-testid="tgl-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>When</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Tone</TableHead>
                                    <TableHead>Tokens</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && (
                                    <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-8">Loading…</TableCell></TableRow>
                                )}
                                {!loading && items.length === 0 && (
                                    <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-8" data-testid="tgl-empty">
                                        No generator activity yet. Hit <strong>Generate with AI</strong> from any invoice template to populate this log.
                                    </TableCell></TableRow>
                                )}
                                {!loading && items.map((row) => (
                                    <TableRow key={row.id} data-testid={`tgl-row-${row.id}`}>
                                        <TableCell className="text-xs whitespace-nowrap text-slate-500">
                                            {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                                        </TableCell>
                                        <TableCell className="text-xs">{row.user_email || row.user_id?.slice(0, 8) || "—"}</TableCell>
                                        <TableCell className="text-sm">{row.name_input || "—"}</TableCell>
                                        <TableCell className="text-xs">
                                            <Badge variant="outline" className="capitalize">{row.category || "—"}</Badge>
                                        </TableCell>
                                        <TableCell className="text-xs capitalize">{row.tone || "—"}</TableCell>
                                        <TableCell className="text-xs font-mono">{row.tokens_used || 0}</TableCell>
                                        <TableCell><StatusChip status={row.status || "failed"} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>
                        Showing {items.length === 0 ? 0 : offset + 1}–{offset + items.length} of {total}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            disabled={offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                            data-testid="tgl-prev-page"
                        >
                            ← Previous
                        </Button>
                        <Button
                            variant="outline"
                            disabled={offset + items.length >= total}
                            onClick={() => setOffset(offset + PAGE_SIZE)}
                            data-testid="tgl-next-page"
                        >
                            Next →
                        </Button>
                    </div>
                </div>
            </div>
        </DashboardShell>
    );
}
